// context/OrgContext.tsx
import { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { collection, getDocs, query, where } from "firebase/firestore";
import { auth, db } from "../config/firebase";
import { Organization } from "./utils/Organization";

interface OrgContextValue {
  orgs: Organization[];
  currentOrgId: string | null;
  setCurrentOrgId: (id: string) => void;
  getCurrentOrgTheme: () => { primary: string; secondary: string };
  getCurrentOrg: () => Organization | undefined;
  loading: boolean;
}

const OrgContext = createContext<OrgContextValue>({
  orgs: [],
  currentOrgId: null,
  setCurrentOrgId: () => {},
  getCurrentOrgTheme: () => ({ primary: "#1976d2", secondary: "#fbc02d" }),
  getCurrentOrg: () => undefined,
  loading: true,
});

export const OrgProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setOrgs([]);
        setCurrentOrgId(null);
        setLoading(false);
        return;
      }

      try {
        const q = query(
          collection(db, "organizations"),
          where("admins", "array-contains", user.uid)
        );
        const snapshot = await getDocs(q);
        const result: Organization[] = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Organization[];

        setOrgs(result);
        if (result.length > 0) {
          setCurrentOrgId(result[0].id); // default to first org
        }
      } catch (err) {
        console.error("Error loading organizations", err);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const getCurrentOrgTheme = () => {
    const current = orgs.find((org) => org.id === currentOrgId);
    return {
      primary: current?.primaryColor || "#ffad48",
      secondary: current?.secondaryColor || "#506180",
    };
  };

  const getCurrentOrg = () => {
    const current = orgs.find((org) => org.id === currentOrgId);
    return current;
  };

  return (
    <OrgContext.Provider
      value={{
        orgs,
        currentOrgId,
        setCurrentOrgId,
        getCurrentOrgTheme,
        getCurrentOrg,
        loading,
      }}
    >
      {children}
    </OrgContext.Provider>
  );
};

export const useOrgContext = () => useContext(OrgContext);
